import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Alert, Select } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { userApi } from '../services/api';
import { generateDeviceFingerprint } from '../utils/deviceFingerprint';
import JSEncrypt from 'jsencrypt';

export default function LoginEnhanced() {
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const publicKeyRef = React.useRef(null);
  const navigate = useNavigate();
  const { t, language, changeLanguage } = useLanguage();

  useEffect(() => {
    // 获取 RSA 公钥
    userApi.getPublicKey().then(({ data }) => {
      publicKeyRef.current = data?.data?.publicKey || null;
    }).catch(() => {});
  }, []);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      // RSA 加密密码
      let password = values.password;
      if (publicKeyRef.current) {
        const encryptor = new JSEncrypt();
        encryptor.setPublicKey(publicKeyRef.current);
        const encrypted = encryptor.encrypt(values.password);
        if (encrypted) password = encrypted;
      }
      const { data } = await userApi.secureLogin({
        username: values.username,
        password,
        deviceFingerprint: generateDeviceFingerprint()
      });
      if (data.code === '200') {
        const loginData = data.data;
        if (loginData.require2FA) {
          setTempToken(loginData.tempToken);
          setShow2FA(true);
          message.info(t('require2FA'));
          setLoading(false);
          return;
        }
        localStorage.setItem('username', loginData.username);
        localStorage.setItem('isLogin', 'true');
        localStorage.setItem('userRole', loginData.role);
        localStorage.setItem('userType', loginData.userType);
        if (loginData.uid) localStorage.setItem('uid', loginData.uid);
        localStorage.setItem('user', JSON.stringify(loginData));
        try {
          const r = await userApi.getMe();
          const me = r.data?.data;
          if (me) {
            if (me.uid) localStorage.setItem('uid', me.uid);
            localStorage.setItem('userChannel', me.channel || '');
            localStorage.setItem('userCurrencies', JSON.stringify(me.currencies || []));
          }
        } catch (_) {}
        message.success(t('loginSuccess'));
        navigate('/');
      } else {
        message.error(data.msg || t('loginFailed'));
      }
    } catch (error) {
      message.error(error.response?.data?.msg || t('loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) { message.error(t('enter6Code')); return; }
    setLoading(true);
    try {
      const { data } = await userApi.verify2FALogin(tempToken, twoFACode);
      if (data.code === '200') {
        const loginData = data.data;
        localStorage.setItem('username', loginData.username);
        localStorage.setItem('isLogin', 'true');
        localStorage.setItem('userRole', loginData.role);
        localStorage.setItem('userType', loginData.userType);
        if (loginData.uid) localStorage.setItem('uid', loginData.uid);
        localStorage.setItem('user', JSON.stringify(loginData));
        // 获取用户渠道权限 & 补全 uid（等待完成后再跳转）
        try {
          const r = await userApi.getMe();
          const me = r.data?.data;
          if (me) {
            if (me.uid) localStorage.setItem('uid', me.uid);
            localStorage.setItem('userChannel', me.channel || '');
            localStorage.setItem('userCurrencies', JSON.stringify(me.currencies || []));
          }
        } catch (_) {}
        message.success(t('loginSuccess'));
        navigate('/');
      } else {
        message.error(data.msg || t('twoFAVerifyFailed'));
      }
    } catch (error) {
      message.error(error.response?.data?.msg || t('twoFAVerifyFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px'
    }}>
      <Card style={{
        width: 450, boxShadow: '0 20px 40px rgba(0,0,0,0.1)', borderRadius: 16,
        border: 'none', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', position: 'relative', zIndex: 1
      }} bodyStyle={{ padding: '48px 40px' }}>
        <Select
          value={language}
          onChange={changeLanguage}
          style={{ position: 'absolute', top: 16, right: 16, width: 120 }}
          size="small"
          suffixIcon={<GlobalOutlined />}
          options={[
            { label: '🇨🇳 中文', value: 'zh-CN' },
            { label: '🇺🇸 English', value: 'en-US' },
          ]}
        />
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px', boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
          }}>
            <SafetyOutlined style={{ fontSize: 36, color: 'white' }} />
          </div>
          <h2 style={{ margin: 0, color: '#1f2937', fontSize: 24 }}>{t('loginTitle')}</h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 8 }}>{t('loginSubtitle')}</p>
        </div>

        {show2FA && (
          <Alert message={t('twoFAVerification')} description={t('twoFADesc')}
            type="info" showIcon style={{ marginBottom: 24 }} />
        )}

        {!show2FA ? (
          <Form onFinish={handleLogin} size="large" layout="vertical">
            <Form.Item name="username" rules={[{ required: true, message: t('usernameRequired') }]} style={{ marginBottom: 24 }}>
              <Input prefix={<UserOutlined style={{ color: '#9ca3af' }} />} placeholder={t('username')}
                style={{ borderRadius: 8, border: '2px solid #e5e7eb', padding: '12px 16px', fontSize: 16 }} />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: t('passwordRequired') }]}
              style={{ marginBottom: 32 }}>
              <Input.Password prefix={<LockOutlined style={{ color: '#9ca3af' }} />} placeholder={t('password')}
                style={{ borderRadius: 8, border: '2px solid #e5e7eb', padding: '12px 16px', fontSize: 16 }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 24 }}>
              <Button type="primary" htmlType="submit" loading={loading} block style={{
                height: 56, borderRadius: 8, fontSize: 18, fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}>
                {loading ? t('loggingIn') : t('login')}
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div>
            <Form.Item style={{ marginBottom: 24 }}>
              <Input placeholder={t('enter6DigitCode')} value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value)} maxLength={6} size="large"
                style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8, borderRadius: 8, border: '2px solid #e5e7eb', padding: '12px 16px' }} />
            </Form.Item>
            <Button type="primary" onClick={handleVerify2FA} loading={loading} block style={{
              height: 56, borderRadius: 8, fontSize: 18, fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)', marginBottom: 16
            }}>
              {loading ? t('verifying') : t('verify')}
            </Button>
            <Button onClick={() => { setShow2FA(false); setTempToken(null); setTwoFACode(''); }} block style={{ height: 48, borderRadius: 8 }}>
              {t('backToLogin')}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
